import {
    CdkDrag,
    CdkDragDrop,
    CdkDragEnd,
    CdkDragHandle,
    CdkDragMove,
    CdkDragStart,
    CdkDropList,
    CdkDropListGroup,
    moveItemInArray,
    transferArrayItem,
} from '@angular/cdk/drag-drop';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, Renderer2, signal } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowsUpDown, faDownLeftAndUpRightToCenter, faGripLinesVertical, faMagnifyingGlass, faMaximize, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { BaseDirective } from 'src/app/core/directives/base.directive';

import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SplitAreaComponent, SplitComponent, SplitDirection, SplitGutterInteractionEvent } from 'angular-split';
import { DocumentSubSection } from 'src/app/core/types/document-sub-section';
import { DocumentType } from 'src/app/models/enums/document-type';
import { PropertyType } from 'src/app/models/enums/property-type';
import { FolderLayout } from 'src/app/models/folder-layout';
import { Section } from 'src/app/models/section';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { CommentInput } from 'src/app/models/server/comment-input';
import { Contact } from 'src/app/models/server/contact';
import { ResizingStatus } from 'src/app/models/server/enums/resizing-status';
import { Rights } from 'src/app/models/server/enums/rights';
import { ViewType } from 'src/app/models/server/enums/view-type';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { Reminder } from 'src/app/models/server/reminder';
import { View } from 'src/app/models/server/view';
import { WorkflowStepDefinition } from 'src/app/models/server/workflow-step-definition';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { EventsManagerService } from 'src/app/services/events/events-manager.service';
import { WorkflowsService } from 'src/app/services/http/workflows.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { DocumentsStateService } from 'src/app/services/state/documents-state.service';
import { FolderLayoutsStateService } from 'src/app/services/state/folder-layouts-state.service';
import { ProfileSettingsStateService } from 'src/app/services/state/profile-settings-state.service';
import { RemindersStateService } from 'src/app/services/state/reminders-state.service';
import { DetailBaseDatasource } from '../datasources/detail-base-datasource';
import { AttributesSectionComponent } from './sections/attributes-section/attributes-section.component';
import { DocumentsSectionComponent } from './sections/documents-section/documents-section.component';
import { HistorySectionComponent } from './sections/history-section/history-section.component';
import { RemindersSectionComponent } from './sections/reminders-section/reminders-section.component';
import { ViewerSectionComponent } from './sections/viewer-section/viewer-section.component';
import { WorkflowSectionComponent } from './sections/workflow-section/workflow-section.component';

/**
 * Composant affichant le détail d'un dossier (avec des sections redimensionnables).
 */
@Component({
    selector: 'app-sections-container',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkDropListGroup,
        CdkDropList,
        CdkDrag,
        NgClass,
        FaIconComponent,
        CdkDragHandle,
        DocumentsSectionComponent,
        AttributesSectionComponent,
        WorkflowSectionComponent,
        HistorySectionComponent,
        RemindersSectionComponent,
        ViewerSectionComponent,
        AsyncPipe,
        SplitComponent,
        SplitAreaComponent,
        CommonModule,
        FormsModule,
        MatTooltipModule,
    ],
    templateUrl: './sections-container.component.html',
    styleUrl: './sections-container.component.scss'
})
export class SectionsContainerComponent extends BaseDirective implements OnInit {
    /**
     * Références des icônes utilisés dans le template.
     */
    public dragAndDropIcon = faGripLinesVertical;
    public removeSectionIcon = faXmark;
    public searchDocumentIcon = faMagnifyingGlass;
    public fullScreenIcon = faMaximize;
    public restoreSizeIcon = faDownLeftAndUpRightToCenter;
    public verticalGrowIcon = faArrowsUpDown;

    /**
     * Références des Enums utilisées dans le template.
     */
    public PropertyType = PropertyType;
    public ViewType = ViewType;
    public ResizingStatus = ResizingStatus;

    /**
     * Dossier dont le détail est affiché.
     */
    private _folder: Folder;

    /**
     * Obtient le dossier dont le détail est affiché.
     */
    get folder(): Folder {
        return this._folder;
    }

    /**
     * Définie le dossier dont le détail est affiché.
     */
    @Input()
    set folder(f: Folder) {
        this._folder = f;
        this.datasource.listReminders(this.folder.viewId, this.folder.id).subscribe((x) => {
            this.remindersStateService.setRemindersList(x);
        });
    }

    /**
     * Vue à laquelle appartient le dossier.
     */
    public _view: View;

    /**
     * Obtient la vue à laquelle appartient le dossier.
     */
    get view(): View {
        return this._view;
    }

    /**
     * Définie la vue à laquelle appartient le dossier.
     */
    @Input()
    set view(v: View) {
        this._view = v;
        this.folderLayout = this.folderLayoutStateService.getfolderLayout(this._view);
    }

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Liste des définitions des attributs .
     */
    @Input()
    public attributesDefinitions: AttributeDefinition[];

    /**
     * Emis lorsque l'utilisateur Supprime un document.
     */
    @Output()
    public documentDeleted: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur répond à un mail.
     */
    @Output()
    public mailReplied: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur renvoit un mail.
     */
    @Output()
    public mailResended: EventEmitter<FolderDocument> = new EventEmitter();

    /**
     * Liste des définitions des étapes de workflow.
     */
    public workflowStepDefinitions$: Observable<WorkflowStepDefinition[]>;

    /**
     * Instance du document selectionné.
     */
    public selectedDocument: FolderDocument = null;

    /**
     * Disposition des sections du dossier.
     */
    public folderLayout: FolderLayout = null;

    /**
     * Valeur indiquant si les dates de changement d'état des étapes de workflow sont visibles.
     */
    public isWorkflowStepDateVisible: boolean;

    /**
     * Sous section de la section 'documents' sélectionnée.
     */
    public subSectionSelected: DocumentSubSection = 'Documents';

    /**
     * Texte recherché dans les documents (Fonctionnalité de recherche).
     */
    public documentSearchText = signal(null);

    /**
     * Texte recherché dans les brouillons de mail (Fonctionnalité de recherche).
     */
    public draftEmailSearchText = signal(null);

    /**
     * Référence de l'élément overlay utilisé par le drag&drop pour indiquer la zone d'insertion d'une section.
     */
    private overlayElement: HTMLElement = null;

    /**
     * Référence de l'élément correspondant à la section survollée par la souris.
     */
    private currentSectionHovered: Element = null;

    /**
     * Index de la section cible lors d'un drag&drop.
     */
    private targetSectionIndex: number = null;

    /**
     * Hauteur du bandeau supérieur de la page.
     * remarque : La position de la souris est relative au document, alors que les positions des elements 'section' sont relatives au conteneur main. Il faut donc retrancher la hauteur du bandeau superieur (50px) aux positions données par la souris.
     */
    private bannerHeight = 50;

    /**
     * Configuration des groupes de sections.
     */
    public sectionsGroupConfiguration: {
        dir: SplitDirection;
        restrictMove: boolean;
        useTransition: boolean;
        gutterSize: number | null;
        gutterStep: number | null;
        width: number | null;
        height: number | null;
    } = {
        dir: 'vertical',
        restrictMove: true,
        useTransition: true,
        gutterSize: 5,
        gutterStep: null,
        width: null,
        height: 100,
    };

    /**
     * Constructeur.
     * @param notificationService
     * @param remindersService
     * @param folderActionsService
     * @param foldersDocumentsService
     * @param connectedUserStateService
     * @param dialogService
     * @param remindersStateService
     * @param documentsStateService
     * @param profileSettingsStateService
     * @param folderLayoutStateService
     * @param ListLayoutsService
     * @param eventsManagerService
     * @param folderCommentsService
     */
    constructor(
        private notificationService: NotificationService,
        private connectedUserStateService: ConnectedUsersStateService,
        private dialogService: DialogService,
        private remindersStateService: RemindersStateService,
        protected documentsStateService: DocumentsStateService,
        protected profileSettingsStateService: ProfileSettingsStateService,
        private folderLayoutStateService: FolderLayoutsStateService,
        protected workflowsService: WorkflowsService,
        private eventsManagerService: EventsManagerService,
        private datasource: DetailBaseDatasource,
        private cdr: ChangeDetectorRef,
        private renderer: Renderer2
    ) {
        super();
    }

    /**
     * Levée à l'initialisation du composant.
     */
    ngOnInit(): void {
        if (!this.folder.boxId) {
            // Dans le cas d'un dossier de workflow, charge la definition des étapes du workflow.
            this.workflowStepDefinitions$ = this.workflowsService.listWorkflowStepDefinitions(this.folder.viewId);
        }

        // Si le dossier n'est pas en lecture seul --> passe le dossier à l'état 'lu'.
        if (this.folder.isRead === false) {
            this.markFolderAsRead();
        }

        // Détermine à partir des paramètres de l'utilisateur si les dates des étapes de workflow sont affichées.
        this.isWorkflowStepDateVisible = this.connectedUserStateService.getUserConnected().params.isWorkflowStepDateVisible === true;

        this.subscribeToEvents();
    }

    /**
     * Fonction de tracking des sections.
     * @param index : index.
     * @param section : Instance de la section.
     * @returns : Un identifiant unique.
     */
    public trackBySectionFct(index: number, section: Section) {
        return section.name;
    }

    /**
     * Appelée lorsque l'utilisateur selectionne le menu 'Marquer comme lu' ou 'Marquer comme non lu' sur un document.
     * @param document : Instance du document.
     */
    public onDocumentMarkedAsRead(document: FolderDocument): void {
        this.datasource
            .markDocumentAsRead(document.viewId, document.folderId, document.id, document.isRead)
            .pipe(
                map((x) => {
                    this.eventsManagerService.emitHistorySectionUpdatedEvent(document.viewId, document.folderId);

                    // this.datasource.listDocuments(document.viewId, document.folderId).subscribe((documents) => {
                    //     this.eventsManagerService.emitDocumentsUpdatedEvent(document.folderId, documents);
                    // });

                    this.eventsManagerService.emitAttributesSectionUpdatedEvent(document.viewId, document.folderId);

                    this.eventsManagerService.emitListFolderUpdatedEvent(document.viewId, document.folderId);
                    this.eventsManagerService.emitFollowedFoldersUpdatedEvent(document.viewId, document.folderId);
                })
            )
            .subscribe();
    }

    /**
     * Appelée lorsque l'utilisateur clique sue le menu contextuel 'Ajouter l'emetteur au carnet d'adresses' depuis un document.
     * @param document : Instance du document.
     */
    public onContactAdded(document: FolderDocument): void {
        if (document.type === DocumentType.Email) {
            let contact = new Contact();
            contact.email = (document as FolderDocument).from;
            this.datasource.addContact(contact).subscribe((x) => this.notificationService.displaySuccess("Le contact a bien été ajouté au carnet d'adresses.", ''));
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le menu contextuel 'Supprimer le document' depuis un document.
     * @param document : Instance du document.
     */
    public onDocumentDeleted(document: FolderDocument): void {
        this.dialogService.openConfirmDeleteDocument(document).subscribe((confirmation: boolean) => {
            if (confirmation === true) {
                this.documentDeleted.emit(document);
            }
        });
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le menu contextuel 'Répondre au mail' depuis un document.
     * @param document : Instance du document.
     */
    public onMailReplied(document: FolderDocument): void {
        this.mailReplied.emit(document);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le menu contextuel 'Renvoyer le mail' depuis un document.
     * @param document : Instance du document.
     */
    public onMailResended(document: FolderDocument): void {
        this.mailResended.emit(document);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton de reinitialisation de la recherche depuis la section 'documents' ou 'brouillons'.
     */
    public onSearchReseted(reset: boolean): void {
        if (this.subSectionSelected === 'Documents') {
            this.documentSearchText.set('');
        } else {
            this.draftEmailSearchText.set('');
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Editer' d'un rappel.
     * @param rappel : Instance du rappel.
     */
    public onReminderEdited(reminder: Reminder): void {
        if (!this.readonly && this.connectedUserStateService.hasRight(Rights.ReminderEdit)) {
            // Copie du reminder pour ne pas modifier directement l'instance --> Deep Clone
            let editableReminder = JSON.parse(JSON.stringify(reminder));

            this.dialogService.openEditReminder(editableReminder).subscribe((data: Reminder) => {
                if (!!data) {
                    this.datasource.updateReminder(this.view.id, this.folder.id, data).subscribe((x) => {
                        let reminders = this.remindersStateService.getRemindersList();

                        const index = reminders.indexOf(reminder);

                        if (index >= 0) {
                            // Remplace le rappel dans la liste.
                            reminders.splice(index, 1, x);
                        }
                        this.remindersStateService.setRemindersList(reminders);

                        this.notificationService.displaySuccess('Le rappel a bien été modifié.', '');
                    });
                }
            });
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Supprimer' d'un rappel.
     * @param rappel : Instance du rappel.
     */
    public onReminderDeleted(reminder: Reminder): void {
        if (!this.readonly && this.connectedUserStateService.hasRight(Rights.ReminderDelete)) {
            this.datasource.deleteReminder(this.view.id, reminder).subscribe((x) => {
                let reminders = this.remindersStateService.getRemindersList();

                const index = reminders.indexOf(reminder);

                if (index >= 0) {
                    // Retire le rappel de la liste.
                    reminders.splice(index, 1);
                }

                this.remindersStateService.setRemindersList(reminders);
                this.notificationService.displaySuccess('Le rappel a bien été supprimé.', '');
            });
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Editer' d'une ligne de commentaire.
     * @param rappel : Instance de la ligne de commentaire.
     */
    public onCommentEdited(comment: CommentInput): void {
        this.datasource.updateComment(this.view.id, this.folder.id, comment).subscribe((x) => {
            this.eventsManagerService.emitCommentChangeEvent(this.folder.id, x);
            this.notificationService.displaySuccess('Le commentaire a bien été modifié.', '');
        });
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Supprimer' d'une ligne de commentaire.
     * @param rappel : Instance de la ligne de commentaire.
     */
    public onCommentDeleted(comment: CommentInput): void {
        this.datasource.deleteComment(this.view.id, this.folder.id, comment).subscribe((x) => {
            this.eventsManagerService.emitCommentChangeEvent(this.folder.id, x);
            this.notificationService.displaySuccess('Le commentaire a bien été supprimé.', '');
        });
    }

    /**
     * Appelé lorsque l'utilisateur déplace une section par glisser/déposer.
     * @param event : Evenement drag&drop.
     */
    public onSectionDrop(event: CdkDragDrop<Section[]>): void {
        /* Attention des index retournés par l'evenement de drag&drop ne sont pas fiables lorsqu'un groupe de sections contient une seule section --> C'est pour cela que l'on redetermine l'index de l'element déplacé et celui de l'élément cible.
        L'index de l'élément déplacé est déterminé à partir de la position de la section dans la liste associée au conteneur (cdkDropListData) */
        let previousIndex = event.previousContainer.data.indexOf(event.item.data);

        if (this.targetSectionIndex === null) {
            if (event.container.data.length === 0) {
                // Le conteneur est vide --> on force la position à 0
                this.targetSectionIndex = 0;
            } else {
                // Le drag&drop se termine à la position de départ --> on annule le drop
                return;
            }
        }

        /* Détermine l'index du groupe de sections correspondant au conteneur source. Dans le cas où le conteneur source est le conteneur des sections non affichées, l'index vaut -1. */
        let sourceContainerIndex = -1;

        if (event.previousContainer.id.startsWith('section-group-')) {
            sourceContainerIndex = Number.parseInt(event.previousContainer.id.substring('section-group-'.length));
        }

        /* Détermine l'index du groupe de sections correspondant au conteneur destination. */
        let targetContainerIndex = Number.parseInt(event.container.id.substring('section-group-'.length));

        if (event.previousContainer === event.container) {
            /* Cas d'un drag&drop dans le même groupe de sections */
            moveItemInArray(event.container.data, previousIndex, this.targetSectionIndex);
        } else {
            if (sourceContainerIndex !== -1) {
                /* Cas d'un drag&drop depuis un groupe de sections */
                transferArrayItem<Section>(
                    this.folderLayout.sectionsGroups[sourceContainerIndex].sections,
                    this.folderLayout.sectionsGroups[targetContainerIndex].sections,
                    previousIndex,
                    this.targetSectionIndex
                );
            } else {
                /* Cas d'un drag&drop depuis le conteneur des sections non affichées */
                event.item.data.sectionsGroup = targetContainerIndex;
                transferArrayItem<Section>(
                    this.folderLayout.nonDisplayedSections,
                    this.folderLayout.sectionsGroups[targetContainerIndex].sections,
                    previousIndex,
                    this.targetSectionIndex
                );
            }
        }
        this.targetSectionIndex = null;
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Masquer' d'une section.
     * @param sectionName : Nom de la section.
     */
    public onRemoveSection(sectionName: string): void {
        this.folderLayout.removeSection(sectionName);
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Restaurer la taille' d'une section.
     */
    public onRestoreSectionSize(sectionName: string): void {
        this.folderLayout.resetSectionSize(sectionName);
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Agrandir en plein écran' d'une section.
     */
    public onDisplaySectionInFullScreen(sectionName: string): void {
        this.folderLayout.displaySectionInFullScreen(sectionName);
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Agrandir verticalement' d'une section.
     */
    public onMaximizeSectionHeight(sectionName: string): void {
        this.folderLayout.maximizeSectionHeight(sectionName);
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * Appelé lorsque l'utilisateur selectionne une sous-section de la section 'Documents'.
     * @param subSectionName : Nom de la sous-section.
     */
    public onSubSectionSelected(subSectionName: DocumentSubSection): void {
        this.subSectionSelected = subSectionName;
        this.cdr.markForCheck();
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Rechercher un document' de la section 'Documents'.
     */
    public onSearch(): void {
        this.dialogService
            .openSearch(
                this.subSectionSelected === 'Documents' ? 'Texte à rechercher dans les documents' : 'Texte à rechercher dans les brouillons de mail',
                this.subSectionSelected === 'Documents' ? this.documentSearchText() : this.draftEmailSearchText()
            )
            .subscribe((search) => {
                if (search && search?.text.length > 0) {
                    this.subSectionSelected === 'Documents' ? this.documentSearchText.set(search.text) : this.draftEmailSearchText.set(search.text);
                }
            });
    }

    /**
     * Appelée lorsque l'utilisateur commence à glisser une section (drag&drop).
     * @param dragStartEvent : Evenemnt drag&drop.
     */
    onDragStart(dragStartEvent: CdkDragStart<Section>): void {
        if (!this.overlayElement) {
            // Crée l'élément overlay utilisé pour indiquer la zone d'insertion d'une section.
            this.overlayElement = this.renderer.createElement('div');
            this.renderer.setProperty(this.overlayElement, 'id', 'overlay');
        }
    }

    /**
     * Appelée lorsque l'utilisateur a fini de glisser une section (drag&drop).
     * @param dragEndEvent : Evenemnt drag&drop.
     */
    onDragEnd(dragEndEvent: CdkDragEnd<Section>): void {
        if (this.overlayElement && this.currentSectionHovered) {
            // Retire l'élément overlay utilisé pour indiquer la zone d'insertion de la section survollée.
            this.renderer.removeChild(this.currentSectionHovered, this.overlayElement);
        }

        this.overlayElement = null;
        this.currentSectionHovered = null;
    }

    /**
     * Appelée lorsque l'utilisateur déplace une section (drag&drop).
     * @param dragMoveEvent : Evenemnt drag&drop.
     */
    onDragMove(dragMoveEvent: CdkDragMove<Section>): void {
        /* Récupère l'élément section survollé par la souris */
        let targetHtmlElement = (dragMoveEvent.event as MouseEvent).target as HTMLElement;
        /* Récupère la section parent de l'élément survollé */
        let sectionElement = targetHtmlElement.closest('.section');

        if (sectionElement) {
            // La souris survolle une zone correspondant à une section.

            let needRefresh: Boolean = false;

            if (this.currentSectionHovered != sectionElement) {
                /* Si la section survollée est différente de la dernière section survollée, on doit rafraichir l'overlay */
                this.currentSectionHovered = sectionElement;
                needRefresh = true;
            }

            // Récupère l'index de la section survollée.
            let index = sectionElement.id.substring('section-'.length);

            // Le haut de l'overlay est aligné avec le haut de la section survollée.
            let overlayTop: number = (sectionElement as any).offsetTop;

            this.targetSectionIndex = Number.parseInt(index);

            if ((dragMoveEvent.event as MouseEvent).clientY - this.bannerHeight > (sectionElement as any).offsetTop + sectionElement.clientHeight / 2) {
                // Si la souris survolle la moitié inférieur de la section, on aligne le haut de l'overlay à la moitié de la section survollée.
                overlayTop = (sectionElement as any).offsetTop + sectionElement.clientHeight / 2;
                this.targetSectionIndex += 1;
            } else {
                // Si la souris survolle la moitié supérieure de la section, on aligne le haut de l'overlay à la moitié de la section survollée.
                overlayTop = (sectionElement as any).offsetTop;
            }

            if (Math.abs(overlayTop - this.overlayElement.offsetTop) > 1 || needRefresh) {
                // Si la position de l'overlay a changé, on rafraichit l'overlay.
                this.renderer.setStyle(this.overlayElement, 'top', `${overlayTop}px`);
                this.renderer.setStyle(this.overlayElement, 'left', `${(sectionElement as any).offsetLeft}px`);
                this.renderer.setStyle(this.overlayElement, 'height', `${sectionElement.clientHeight / 2}px`);
                this.renderer.setStyle(this.overlayElement, 'width', `${sectionElement.clientWidth}px`);

                this.renderer.appendChild(sectionElement, this.overlayElement);
            }
        } else {
            // La souris survolle une zone qui ne correspond pas à une section.
            if (this.overlayElement && this.currentSectionHovered) {
                // On retire l'overlay de la dernière section survollée
                this.renderer.removeChild(this.currentSectionHovered, this.overlayElement);
            }

            this.currentSectionHovered = null;
            this.targetSectionIndex = null;
        }
    }

    /**
     * Appelée lorsque l'utilisateur redimensionne un groupe de sections.
     * @param event : Evenement de redimensionnement.
     */
    public onSectionsGroupResized(event: SplitGutterInteractionEvent): void {
        this.folderLayout.sectionsGroups.forEach((group, index) => {
            group.size = event.sizes[index] as number;
        });

        // Stocke le changement de taille dans le local storage.
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * Appelée lorsque l'utilisateur redimensionne une section.
     * @param sectionGroupIndex : Index du groupe de sections auquel appartient la section redimensionnée.
     * @param event : Evenement de redimensionnement.
     */
    public onSectionResized(sectionGroupIndex: number, event: SplitGutterInteractionEvent): void {
        this.folderLayout.sectionsGroups[sectionGroupIndex].sections.forEach((section, index) => {
            section.size = event.sizes[index] as number;
        });

        // Stocke le changement de taille dans le local storage.
        this.folderLayoutStateService.setFolderLayout(this.view.id, this.folderLayout);
    }

    /**
     * * S'abonne aux événements des autres composants.
     */
    private subscribeToEvents(): void {
        // S'abonne aux evenements de changement de document selectionné.
        this.documentsStateService.onSelectedDocumentChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((documentsByFolderMap) => {
            if (documentsByFolderMap) {
                if (documentsByFolderMap[this.folder.id]) {
                    this.selectedDocument = documentsByFolderMap[this.folder.id];
                    this.cdr.markForCheck();
                }
            }
        });

        // S'abonne aux evenements de changement de selection de la sous-section
        this.documentsStateService.onSelectedSubSectionChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((subSectionName) => {
            if (subSectionName) {
                this.onSubSectionSelected(subSectionName);
            }
        });
    }

    /**
     * Marque le dossier comme lu.
     */
    private markFolderAsRead(): void {
        if (!this.readonly && this.connectedUserStateService.hasRight(Rights.toggleReadState)) {
            this.datasource.markFolderAsRead(this.folder, true);
        }
    }
}
